/**
 * Institution Module Action Forms
 *
 * Standalone sub-forms for institution-owned workflow steps.
 * Each form is only shown when the module is in the Active state
 * (start_flag=true, complete_flag=null) per the Elastic Rendering pattern.
 */
import React, { useState, useEffect } from "react";
import type { AuctionInfo } from "@auction-eth/shared-types";
import { api } from "../services/api.js";
import { getCurrentUserId } from "../services/auth.js";
import { CatalogListingPhotosPicker } from "../components/CatalogListingPhotosPicker.js";

const fieldStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px", border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)", background: "var(--bg)", color: "var(--ink)",
  fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
};
const lblStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
  color: "var(--muted)", display: "block", marginBottom: 5,
};
const sectionHeadStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em",
  color: "var(--brand)", margin: "20px 0 12px", paddingBottom: 6,
  borderBottom: "1px solid var(--border)",
};
const gridStyle: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 4,
};
const infoBoxStyle: React.CSSProperties = {
  background: "var(--brand-soft, #e8f0ff)", border: "1px solid var(--brand)",
  borderRadius: "var(--radius-sm)", padding: "10px 14px", marginBottom: 16,
  fontSize: 13, color: "var(--ink)", lineHeight: 1.5,
};

/** Deduped catalog image URLs in stable order (from field Step 9). */
export function normalizeCatalogingImageUrls(raw: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of raw) {
    const t = typeof u === "string" ? u.trim() : "";
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function initialListingPhotoSelection(
  catalog: string[],
  saved: Partial<AuctionInfo> | null | undefined
): string[] {
  if (catalog.length === 0) return [];
  const savedList = saved?.image_urls;
  if (Array.isArray(savedList) && savedList.length > 0) {
    const picked = catalog.filter((u) => savedList.some((s) => s === u));
    if (picked.length > 0) return picked;
  }
  return [...catalog];
}

// ─── Auction Approval (Step 10) ─────────────────────────────────────────────
/**
 * Two-step submit:
 *  1. PATCH /pre-auction-setup → persists auction_info to case.auction_info
 *  2. PATCH /flags            → sets auction_approval_completed = true
 *
 * Locked fields: initial_price, start_time, end_time, bid_security_type, min_increment
 * are required before the approval button becomes active.
 *
 * The edit is locked by the backend after listing_completed (when the auction
 * is posted to the legacy platform) — not just after auction_approval_completed.
 */
export function AuctionApprovalForm({
  caseId,
  initialAuctionInfo,
  catalogingPhotoUrls,
  onApproved,
}: {
  caseId: string;
  initialAuctionInfo?: Partial<AuctionInfo> | null;
  /** From `module_payloads.step_9_cataloging`; institution narrows these for the legacy listing. */
  catalogingPhotoUrls?: string[];
  onApproved: () => void;
}): React.ReactElement {
  const catalog = normalizeCatalogingImageUrls(catalogingPhotoUrls ?? []);
  const [form, setForm] = useState<Partial<AuctionInfo>>(initialAuctionInfo ?? {});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listingPhotos, setListingPhotos] = useState<string[]>(() =>
    initialListingPhotoSelection(catalog, initialAuctionInfo ?? undefined)
  );

  const catalogKey = catalog.join("\u0001");
  const savedPhotoKey = Array.isArray(initialAuctionInfo?.image_urls)
    ? initialAuctionInfo.image_urls.join("\u0001")
    : "";

  useEffect(() => {
    setListingPhotos(initialListingPhotoSelection(catalog, initialAuctionInfo ?? undefined));
  }, [catalogKey, savedPhotoKey]);

  function set(key: keyof AuctionInfo, value: unknown): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toLocalInput(iso: string | null | undefined): string {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const pad = (n: number) => String(n).padStart(2, "0");
      // Use local-time getters so the picker shows the user's clock time, not UTC.
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  }

  function fromLocalInput(local: string): string {
    // datetime-local value has no TZ suffix → JS parses it as local time →
    // toISOString() correctly converts to UTC for storage.
    return local ? new Date(local).toISOString() : "";
  }

  function isValid(): boolean {
    if (!form.initial_price || form.initial_price <= 0) return false;
    if (!form.start_time) return false;
    if (!form.end_time) return false;
    if (form.end_time <= (form.start_time ?? "")) return false;
    if (!form.bid_security_type) return false;
    if (!form.min_increment || form.min_increment <= 0) return false;
    if (catalog.length > 0 && listingPhotos.length === 0) return false;
    return true;
  }

  async function handleSubmit(): Promise<void> {
    if (!isValid()) {
      setError(
        catalog.length > 0 && listingPhotos.length === 0
          ? "Select at least one catalog photo to include in the marketplace listing."
          : "Please fill in all required fields and ensure End Time is after Start Time."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const listingMedia: Partial<AuctionInfo> =
        catalog.length > 0
          ? {
              image_urls: listingPhotos,
              thumbnail_url: listingPhotos[0] ?? null,
            }
          : {};
      const auctionPayload: Partial<AuctionInfo> = {
        ...form,
        currency: form.currency || "ETB",
        is_closed: form.is_closed ?? false,
        ...listingMedia,
      };
      await api.cases.updatePreAuctionSetup(caseId, auctionPayload, getCurrentUserId());
      await api.cases.updateFlag(caseId, {
        application: "institution",
        flag: "auction_approval_completed",
        value: true,
        actor_id: getCurrentUserId(),
      });
      onApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save and approve auction");
    } finally {
      setSubmitting(false);
    }
  }

  const secType = form.bid_security_type ?? "FIXED";

  return (
    <div>
      <div style={infoBoxStyle}>
        Review the expert valuation, catalog, and photos in the case tabs. Set the auction parameters — they are saved to the case and sent to the marketplace when you approve. Once posted to the platform the parameters are locked.
      </div>

      {catalog.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <p style={sectionHeadStyle}>Photos for marketplace listing</p>
          <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
            Tick the DAA catalog photos to include on the legacy auction listing. Order follows the catalog; the first selected image is the product thumbnail.
          </p>
          <CatalogListingPhotosPicker
            catalogUrls={catalog}
            selectedUrls={listingPhotos}
            onChange={setListingPhotos}
            disabled={submitting}
          />
        </div>
      )}

      {/* ── Financial Parameters ─────────────────────────────────── */}
      <p style={sectionHeadStyle}>Auction Financial Parameters</p>
      <div style={gridStyle}>
        <div>
          <label htmlFor="aa-price" style={lblStyle}>Starting Price (ETB) *</label>
          <input id="aa-price" type="number" min={0} style={fieldStyle}
            value={form.initial_price ?? ""}
            onChange={(e) => set("initial_price", Number(e.target.value))}
            placeholder="e.g. 150000" />
        </div>
        <div>
          <label htmlFor="aa-currency" style={lblStyle}>Currency</label>
          <select id="aa-currency" style={fieldStyle}
            value={form.currency ?? "ETB"}
            onChange={(e) => set("currency", e.target.value)}>
            <option value="ETB">ETB — Ethiopian Birr</option>
            <option value="USD">USD — US Dollar</option>
          </select>
        </div>
        <div>
          <label htmlFor="aa-min-inc" style={lblStyle}>Min Bid Increment (ETB) *</label>
          <input id="aa-min-inc" type="number" min={0} style={fieldStyle}
            value={form.min_increment ?? ""}
            onChange={(e) => set("min_increment", Number(e.target.value))}
            placeholder="e.g. 1000" />
        </div>
        <div>
          <label htmlFor="aa-max-inc" style={lblStyle}>Max Bid Increment (ETB)</label>
          <input id="aa-max-inc" type="number" min={0} style={fieldStyle}
            value={form.max_increment ?? ""}
            onChange={(e) => set("max_increment", e.target.value === "" ? null : Number(e.target.value))}
            placeholder="e.g. 10000 (optional)" />
        </div>
      </div>

      {/* ── Bid Security ─────────────────────────────────────────── */}
      <p style={sectionHeadStyle}>Bid Security</p>
      <div style={gridStyle}>
        <div>
          <label htmlFor="aa-sec-type" style={lblStyle}>Bid Security Type *</label>
          <select id="aa-sec-type" style={fieldStyle}
            value={secType}
            onChange={(e) => set("bid_security_type", e.target.value as AuctionInfo["bid_security_type"])}>
            <option value="FIXED">Fixed Amount (CPO)</option>
            <option value="PERCENTAGE">Percentage of Starting Price</option>
          </select>
        </div>
        {secType === "FIXED" ? (
          <div>
            <label htmlFor="aa-sec-amount" style={lblStyle}>Security Amount (ETB) *</label>
            <input id="aa-sec-amount" type="number" min={0} style={fieldStyle}
              value={form.min_amount_to_bid ?? ""}
              onChange={(e) => set("min_amount_to_bid", Number(e.target.value))}
              placeholder="e.g. 5000" />
          </div>
        ) : (
          <div>
            <label htmlFor="aa-sec-pct" style={lblStyle}>Security Percentage (%) *</label>
            <input id="aa-sec-pct" type="number" min={0} max={100} style={fieldStyle}
              value={form.percentage_bid_security ?? ""}
              onChange={(e) => set("percentage_bid_security", Number(e.target.value))}
              placeholder="e.g. 10" />
          </div>
        )}
      </div>

      {/* ── Schedule ─────────────────────────────────────────────── */}
      <p style={sectionHeadStyle}>Auction Schedule</p>
      <div style={gridStyle}>
        <div>
          <label htmlFor="aa-start" style={lblStyle}>Bidding Opens *</label>
          <input id="aa-start" type="datetime-local" style={fieldStyle}
            value={toLocalInput(form.start_time)}
            onChange={(e) => set("start_time", fromLocalInput(e.target.value))} />
        </div>
        <div>
          <label htmlFor="aa-end" style={lblStyle}>Bidding Closes *</label>
          <input id="aa-end" type="datetime-local" style={fieldStyle}
            value={toLocalInput(form.end_time)}
            onChange={(e) => set("end_time", fromLocalInput(e.target.value))} />
        </div>
        <div>
          <label htmlFor="aa-closed" style={lblStyle}>Bidding Format</label>
          <select id="aa-closed" style={fieldStyle}
            value={form.is_closed ? "closed" : "open"}
            onChange={(e) => set("is_closed", e.target.value === "closed")}>
            <option value="open">Open (live visible bids)</option>
            <option value="closed">Sealed (hidden bids)</option>
          </select>
        </div>
      </div>

      {/* ── Approval Notes ───────────────────────────────────────── */}
      <p style={sectionHeadStyle}>Approval Notes</p>
      <div style={{ marginBottom: 18 }}>
        <label htmlFor="aa-notes" style={lblStyle}>Notes (optional)</label>
        <textarea id="aa-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Any conditions or observations for this approval…"
          style={{ ...fieldStyle, resize: "vertical" }} />
      </div>

      {!isValid() && (
        <p style={{ fontSize: 12, color: "var(--warn)", margin: "0 0 10px" }}>
          * Starting price, start/end times, bid security type, min increment, and (when catalog photos exist) at least one listing photo are required.
        </p>
      )}
      {error && <p role="alert" style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 10px" }}>{error}</p>}

      <button
        onClick={() => void handleSubmit()}
        disabled={submitting || !isValid()}
        aria-label="Save auction parameters and approve for listing"
        style={{
          width: "100%", padding: "13px", borderRadius: "var(--radius-sm)", border: "none",
          background: submitting || !isValid() ? "var(--muted)" : "var(--brand)",
          color: "#fff", fontSize: 15, fontWeight: 700,
          cursor: submitting || !isValid() ? "not-allowed" : "pointer",
        }}>
        {submitting ? "Saving & Approving…" : "✓ Save Parameters & Approve for Listing"}
      </button>
    </div>
  );
}

// ─── Approved Bidding (Step 13) ─────────────────────────────────────────────
export function ApprovedBiddingForm({
  caseId,
  platformAuctionId,
  onApproved,
}: {
  caseId: string;
  platformAuctionId: string | null;
  onApproved: () => void;
}): React.ReactElement {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleApprove(): Promise<void> {
    const plat = platformAuctionId?.trim();
    if (!plat) {
      setError("This case has no marketplace auction ID yet — complete listing first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const actorId = getCurrentUserId();
      const output: Record<string, unknown> = {
        case_id: caseId,
        platform_auction_id: plat,
        bidding_approved: true,
      };
      if (notes.trim()) output.inspection_notes = notes.trim();
      await api.cases.submitModule(caseId, "approved_bidding", output, actorId);
      onApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve bidding");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 0 }}>
        The asset is listed and inspection is complete. Formally authorise the platform to open live bidding.
      </p>
      <div style={{ marginBottom: 14 }}>
        <label htmlFor="ab-notes" style={lblStyle}>Authorization Notes (optional)</label>
        <textarea id="ab-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Any notes for the bidding authorization…" style={{ ...fieldStyle, resize: "vertical" }} />
      </div>
      {error && <p role="alert" style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 10px" }}>{error}</p>}
      <button onClick={() => void handleApprove()} disabled={submitting}
        aria-label="Authorize live bidding"
        style={{ width: "100%", padding: "13px", borderRadius: "var(--radius-sm)", border: "none",
          background: submitting ? "var(--muted)" : "#f59e0b", color: "#fff", fontSize: 15,
          fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer" }}>
        {submitting ? "Authorizing…" : "✓ Authorize Live Bidding"}
      </button>
    </div>
  );
}

// ─── Winner Approval (Step 16) ──────────────────────────────────────────────

export type WinnerBidRow = {
  bid_id?: number;
  user_id?: number;
  username?: string;
  fullname?: string;
  amount?: number;
  status?: string;
  submitted_at?: string;
};

export function WinnerApprovalForm({
  caseId,
  winnerId,
  winningBidAmount,
  winnerFullname,
  winnerUsername,
  bidRows,
  platformAuctionId,
  onApproved,
}: {
  caseId: string;
  winnerId: string | null;
  /** From platform poller (`platform_data.winning_amount`). */
  winningBidAmount: number | null;
  winnerFullname?: string | null;
  winnerUsername?: string | null;
  bidRows?: WinnerBidRow[];
  platformAuctionId: string | null;
  onApproved: () => void;
}): React.ReactElement {
  const rows = bidRows ?? [];
  const displayName =
    [winnerFullname?.trim(), winnerUsername?.trim()].filter(Boolean).join(" · ") || null;
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleApprove(): Promise<void> {
    if (!winnerId?.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const actorId = getCurrentUserId();
      await api.cases.submitModule(caseId, "winner_approval", {
        case_id: caseId,
        winner_participant_id: winnerId.trim(),
        winning_bid: winningBidAmount ?? 0,
        winner_approved: true,
        approval_notes: notes.trim() || "",
      }, actorId);
      onApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve winner");
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = Boolean(winnerId?.trim());
  return (
    <div>
      <dl style={{ margin: "0 0 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div>
          <dt style={lblStyle}>Proposed winner (marketplace)</dt>
          <dd style={{ margin: "3px 0 0", fontSize: 14, color: "var(--ink)" }}>
            {displayName ?? "—"}
            {winnerId?.trim() ? (
              <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                Platform user ID: {winnerId}
              </span>
            ) : (
              <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                Awaiting platform result — open the Auction Info tab to view synced bids.
              </span>
            )}
          </dd>
        </div>
        {winningBidAmount != null && (
          <div><dt style={lblStyle}>Winning bid</dt><dd style={{ margin: "3px 0 0", fontSize: 14, color: "var(--ink)", fontWeight: 700 }}>ETB {winningBidAmount.toLocaleString()}</dd></div>
        )}
        {platformAuctionId ? (
          <div><dt style={lblStyle}>Platform auction</dt><dd style={{ margin: "3px 0 0", fontSize: 14, color: "var(--ink)" }}>{platformAuctionId}</dd></div>
        ) : null}
      </dl>
      {rows.length > 0 ? (
        <div style={{ marginBottom: 16, overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--panel-soft, var(--bg))" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", color: "var(--muted)" }}>
                <th style={{ padding: "8px 10px" }}>Bidder</th>
                <th style={{ padding: "8px 10px" }}>ETB</th>
                <th style={{ padding: "8px 10px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isWinner = i === 0 && (row.amount ?? 0) > 0;
                return (
                <tr
                  key={`${row.bid_id ?? i}-${row.user_id ?? ""}`}
                  style={{
                    borderBottom: "1px solid var(--border)",
                    background: isWinner ? "var(--good-bg, #e9f8ee)" : undefined,
                  }}
                >
                  <td style={{ padding: "8px 10px", color: "var(--ink)" }}>
                    {row.fullname || row.username || `User #${row.user_id ?? "—"}`}
                    {isWinner ? (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: "var(--good)" }}>WINNER</span>
                    ) : null}
                  </td>
                  <td style={{ padding: "8px 10px", fontWeight: 600 }}>{row.amount != null ? row.amount.toLocaleString() : "—"}</td>
                  <td style={{ padding: "8px 10px", color: "var(--muted)" }}>{row.status ?? "—"}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <label htmlFor="wa-notes" style={lblStyle}>Approval Notes (optional)</label>
      <textarea id="wa-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Conditions or notes for winner approval…" style={{ ...fieldStyle, resize: "vertical", marginBottom: 12 }} />
      {error && <p role="alert" style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 10px" }}>{error}</p>}
      <button onClick={() => void handleApprove()} disabled={submitting || !canSubmit}
        aria-label="Approve winner"
        style={{ width: "100%", padding: "13px", borderRadius: "var(--radius-sm)", border: "none",
          background: submitting || !canSubmit ? "var(--muted)" : "var(--brand)", color: "#fff",
          fontSize: 15, fontWeight: 700, cursor: submitting || !canSubmit ? "not-allowed" : "pointer" }}>
        {submitting ? "Approving…" : "✓ Approve Winner"}
      </button>
      {!canSubmit ? (
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 6, textAlign: "center", lineHeight: 1.5 }}>
          AEOS is still syncing the hammer result from the marketplace. If this persists, confirm LEGACY_API_URL and that
          the auction closed on the platform.
        </p>
      ) : null}
    </div>
  );
}

// ─── Settlement (Step 18) ────────────────────────────────────────────────────
export function SettlementForm({
  caseId,
  winnerParticipantId,
  winningBidAmount,
  onSettled,
}: {
  caseId: string;
  winnerParticipantId: string | null;
  winningBidAmount: number | null;
  onSettled: () => void;
}): React.ReactElement {
  const [ref, setRef] = useState("");
  const [amount, setAmount] = useState("");
  const [officer, setOfficer] = useState("");
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (winningBidAmount != null && Number.isFinite(winningBidAmount)) {
      setAmount(String(winningBidAmount));
    }
  }, [winningBidAmount]);

  async function handleSettle(): Promise<void> {
    if (!winnerParticipantId?.trim()) {
      setError("Winner is not recorded on this case — cannot settle.");
      return;
    }
    if (!ref.trim() || !amount.trim() || !officer.trim()) {
      setError("Settlement reference, amount, and confirming officer are required.");
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) {
      setError("Enter a valid settled amount.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const actorId = getCurrentUserId();
      const notesCombined = memo.trim()
        ? memo.trim()
        : `Settlement ref ${ref.trim()}; officer ${officer.trim()}`;
      await api.cases.submitModule(caseId, "settlement", {
        case_id: caseId,
        winner_participant_id: winnerParticipantId.trim(),
        winning_bid: winningBidAmount ?? 0,
        payment_confirmed: true,
        settlement_reference: ref.trim(),
        settlement_amount: amt,
        currency: "ETB",
        confirming_officer: officer.trim(),
        notes: notesCombined,
      }, actorId);
      onSettled();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record settlement");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, lineHeight: 1.5 }}>
        Winner: {winnerParticipantId ?? "—"}
        {winningBidAmount != null ? ` · Auction hammer ETB ${winningBidAmount.toLocaleString()}` : ""}
      </p>
      <div><label htmlFor="st-ref" style={lblStyle}>Settlement Reference *</label>
        <input id="st-ref" type="text" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. SETTLE-2026-001" style={fieldStyle} /></div>
      <div><label htmlFor="st-amount" style={lblStyle}>Settled Amount (ETB) *</label>
        <input id="st-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Pre-filled from auction hammer; adjust if needed" style={fieldStyle} /></div>
      <div><label htmlFor="st-officer" style={lblStyle}>Confirmed By (Officer Name) *</label>
        <input id="st-officer" type="text" value={officer} onChange={(e) => setOfficer(e.target.value)} placeholder="Confirming officer name" style={fieldStyle} /></div>
      <div><label htmlFor="st-memo" style={lblStyle}>Notes (optional)</label>
        <textarea id="st-memo" rows={2} value={memo} onChange={(e) => setMemo(e.target.value)}
          placeholder="Additional settlement notes…" style={{ ...fieldStyle, resize: "vertical" }} /></div>
      {error && <p role="alert" style={{ color: "var(--danger)", fontSize: 13 }}>{error}</p>}
      <button onClick={() => void handleSettle()} disabled={submitting}
        aria-label="Record settlement and close case"
        style={{ padding: "13px", borderRadius: "var(--radius-sm)", border: "none",
          background: submitting ? "var(--muted)" : "var(--good)", color: "#fff",
          fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer" }}>
        {submitting ? "Recording…" : "Record Settlement & Close Case"}
      </button>
    </div>
  );
}
